import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Info, ArrowRight, Loader2 } from "lucide-react";

const LEAD_KEY = "sz_sayisal_lead_id";
const CLAIM_TICKET_KEY = "sz_sayisal_claim_ticket";

export default function DiagnosticResults() {
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState(null);
  const [convertLoading, setConvertLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const lead = localStorage.getItem(LEAD_KEY);
    if (!lead) return;
    (async () => {
      try {
        const res = await api.get("/sayisal/progress", {
          headers: { "X-Anonymous-Lead-Id": lead },
        });
        setProgress(res.data);
      } catch {
        /* insufficient-evidence surface renders empty */
      }
      try {
        const h = await api.get("/sayisal/history", {
          headers: { "X-Anonymous-Lead-Id": lead },
        });
        setHistory(h.data);
      } catch {
        /* history optional */
      }
      // Fire the registration_handoff_started event best-effort.
      try {
        await api.post("/sayisal/funnel-events", {
          owner_key: `lead:${lead}`,
          event_type: "registration_handoff_started",
        });
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  const status = progress?.status || "insufficient_evidence";
  const counts = progress?.counts || {
    diagnostic_sessions_completed: 0,
    diagnostic_sessions_ready: 0,
    attempts_recorded: 0,
  };

  const onConvert = async () => {
    const lead = localStorage.getItem(LEAD_KEY);
    if (!lead) {
      navigate("/kayit");
      return;
    }
    setConvertLoading(true);
    try {
      const res = await api.post("/sayisal/lead/claim-ticket", {
        anonymous_lead_id: lead,
      });
      const ticket = res.data?.claim_ticket;
      if (ticket) {
        // Store the plaintext ticket in sessionStorage for the very
        // next page. It is short-lived, single-use, opaque and never
        // sent back to any other origin.
        sessionStorage.setItem(CLAIM_TICKET_KEY, ticket);
        navigate(`/kayit?claim_ticket=${encodeURIComponent(ticket)}`);
        return;
      }
    } catch {
      /* fall through */
    } finally {
      setConvertLoading(false);
    }
    navigate("/kayit");
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="diagnostic-results-page">
      <PublicHeader />
      <main className="flex-1 max-w-3xl mx-auto px-5 lg:px-8 py-16 lg:py-24 w-full">
        <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">
          Tanılama Sonucu
        </div>
        <h1 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-zinc-900">
          Ham kanıt — yorum yok.
        </h1>
        <p className="mt-4 text-zinc-600 leading-relaxed">
          Aşağıdaki sayılar oturumundan yalnızca <em>ne olduğunu</em> gösterir; içerik motoru aktif
          olmadığı için <em>neden</em> olduğuna dair güvenilir bir yorum henüz üretilemez. İçerik
          motoru bağımsız uzmanlık akışında değerlendirildikçe bu ekran yorumla zenginleşir.
        </p>

        <div className="mt-8 rounded-2xl border border-zinc-200 bg-zinc-50 p-6" data-testid="progress-summary">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-zinc-500" />
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Durum: {status} · Zeka yorumu: insufficient_intelligence
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-2xl font-display font-bold text-zinc-900">
                {counts.diagnostic_sessions_completed}
              </div>
              <div className="text-xs text-zinc-500 mt-1">Tamamlanan tanılama</div>
            </div>
            <div>
              <div className="text-2xl font-display font-bold text-zinc-900">
                {counts.diagnostic_sessions_ready}
              </div>
              <div className="text-xs text-zinc-500 mt-1">Hazır oturum</div>
            </div>
            <div>
              <div className="text-2xl font-display font-bold text-zinc-900">
                {counts.attempts_recorded}
              </div>
              <div className="text-xs text-zinc-500 mt-1">Kaydedilmiş cevap</div>
            </div>
          </div>
        </div>

        {history && history.summary && history.summary.attempts_total > 0 && (
          <div
            className="mt-6 rounded-2xl border border-zinc-200 bg-white p-6"
            data-testid="factual-history-block"
          >
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
              Geçmiş kanıt
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-lg font-display font-bold text-zinc-900">
                  {history.summary.sessions_completed}
                </div>
                <div className="text-xs text-zinc-500">Tamamlanan oturum</div>
              </div>
              <div>
                <div className="text-lg font-display font-bold text-zinc-900">
                  {history.summary.attempts_total}
                </div>
                <div className="text-xs text-zinc-500">Toplam cevap</div>
              </div>
              <div>
                <div className="text-lg font-display font-bold text-zinc-900">
                  {history.summary.distinct_items_attempted}
                </div>
                <div className="text-xs text-zinc-500">Farklı soru</div>
              </div>
              <div>
                <div className="text-lg font-display font-bold text-zinc-900">
                  {history.summary.evaluations.correct}/{history.summary.attempts_total}
                </div>
                <div className="text-xs text-zinc-500">Doğrulanan</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-zinc-400">
              Tekrar sinyali durumu: {history.recurrence?.recurrence_status || "unavailable"}
              {" · "}Nedensel yorum: {history.interventions?.causal_status || "insufficient_evidence"}
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onConvert}
            disabled={convertLoading}
            data-testid="convert-register-cta"
            className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-12 px-7"
          >
            {convertLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-1" />
            )}
            Kayıt Ol ve Haber Al
          </Button>
          <Link to="/tanilama" data-testid="retry-diagnostic-link" className="self-center text-sm text-zinc-500">
            Farklı ders / sınav dene
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
